import React, { useCallback, useEffect, useState, useMemo } from "react";
import Styles from "./ScheduleModal.module.css";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Modal, Tooltip } from "@mui/material";
import { ReactComponent as InfoIcon } from "../../../assets/images/info.svg";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../../commons/Accordion";
import { ReactComponent as ExpandMoreIcon } from "../../../assets/images/expandMoreIcon.svg";
import { Close } from "@mui/icons-material";
import ScheduleUpdateModal from "./ScheduleUpdateModal";
import {
  deliverSchedule_data,
  deliverStatusUpdate,
  editSchedule_data,
  updateRepScheduleStatus,
  viewBlock,
} from "../../../Services/Schedule/schedule";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../commons/Toast/ErrorMessage";
import { fetchSchedule } from "../../../Redux/Slices/scheduleSlice";
import { toast } from "react-toastify";
import { formatToDecimals } from "../../../helpers/dateFormat";
import Settlement from "./Settlement/Settlement";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import DamStrip from "../ConGen/Connection/DamStrip";
import { generateObligationDocument } from "../../../Services/ApiCalling/Listing";
import { convertDate } from "../../../commons/FormatDate";

function getButtonVisibility(scheduleDate, fromTime, toTime) {
  const now = new Date();

  const [day, month, year] = scheduleDate?.split("-").map(Number);

  const createDateWithTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    const date = new Date(year, month - 1, day); // Month is 0-indexed in JS
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const scheduleStartTime = createDateWithTime(fromTime);
  let scheduleEndTime = createDateWithTime(toTime);

  if (scheduleEndTime < scheduleStartTime) {
    scheduleEndTime.setDate(scheduleEndTime.getDate() + 1);
  }

  const twoHoursInMs = 2 * 60 * 60 * 1000;
  const editCutoffTime = new Date(scheduleStartTime.getTime() - twoHoursInMs);
  const showEdit = now < editCutoffTime;

  const showConfirm = now > scheduleEndTime;

  return { showEdit, showConfirm };
}

const ScheduleModal = ({ open, handleClose, connectionId, connection }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state?.scheduleSlice);
  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole,
  )?.toLowerCase();
  const [expanded, setExpanded] = useState(0);
  const [editSchedule, setEditSchedule] = useState(false);
  const [typeCall, setTypeCall] = useState("");
  const [editData, setEditData] = useState([]);
  const [blockLists, setBlockLists] = useState({}); // key: schedule id, value: block list
  const [settlement, setSettlement] = useState({
    status: false,
    id: "",
    data: "",
    initPayment: false,
    islast: false,
  });

  const [selectedScheduleId, setSelectedScheduleId] = useState(null);

  const [initpay, setInitpay] = useState({
    id: "",
    scheduleId: "",
    isLast: false,
  });
  const [confirmModal, setConfirmModal] = useState({
    status: false,
    data: "",
    note: "",
  });

  const handleViewBlock = useCallback(async (id) => {
    if (!id) return;
    let res = await viewBlock(id);
    if (res?.data?.code === 200) {
      setBlockLists(res?.data?.data);
    }
  }, []);

  const handleGenerateObligation = async ({ id, e }) => {
    e.stopPropagation();
    e.preventDefault();
    if (!id) return;
    try {
      const response = await generateObligationDocument(id);
      if (!response?.data) {
        return ErrorMessage("Please try again");
      }
      const blob = response.data;
      const url = window.URL.createObjectURL(
        new Blob([blob], {
          type: "application/pdf",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Obligation.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(
        "Obligation document generated and downloaded successfully!",
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to generate Obligation document.",
      );
    }
  };

  useEffect(() => {
    const id = data?.list?.[0]?.id;
    if (id) {
      handleViewBlock(id);
    }
  }, [data?.list, handleViewBlock]);

  const handleAccordionChange = (index, id) => {
    if (expanded === index) {
      setExpanded("");
    } else {
      setExpanded(index);
      if (!blockLists[id]) {
        handleViewBlock(id);
      }
    }
  };

  const handleEditSchedule = (e, data, type) => {
    e.preventDefault();
    e.stopPropagation();
    handleViewBlock(data?.id);
    setTimeout(() => {
      setTypeCall(type);
      setEditData(data);
      setEditSchedule(true);
    }, 2000);
  };

  const handleUpdateSubmit = async (upperData) => {
    try {
      const totalDeliveredVolume = upperData?.reduce((sum, currentItem) => {
        return sum + (currentItem?.scheduleVolume || 0);
      }, 0);
      let payload = {
        scheduleVolume: totalDeliveredVolume,
        blocks: upperData,
      };
      let res = await editSchedule_data(editData?.id, payload);
      if (res?.data?.code === 200) {
        SuccessMessage(res?.data?.message);
        dispatch(fetchSchedule(connectionId));
        handleAccordionChange(expanded);
        setEditData([]);
      }
    } catch (error) {
      console.error("Error updating schedule:", error);
      toast.error("Failed to update schedule.");
    } finally {
      setEditSchedule(false);
      setEditData([]);
    }
  };

  const handleDeliveredSubmit = async (data) => {
    const totalDeliveredVolume = data.reduce((sum, currentItem) => {
      return sum + (currentItem.deliveredVolume || 0);
    }, 0);
    let payload = {
      deliveredVolume: totalDeliveredVolume,
      blocks: data,
    };
    let res = await deliverSchedule_data(editData?.id, payload);
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message);
      setEditSchedule(false);
      handleAccordionChange(expanded);
      dispatch(fetchSchedule(connectionId));
    }
  };

  const handleDeliveryStatus = async ({
    id,
    scheduleId,
    settlement,
    payment,
  }) => {
    if (settlement?.initPayment) {
      handleInitPay();
      return;
    }
    let payload = {
      id: id,
      scheduleId: scheduleId,
      paymentInitiated: payment,
      settlement: settlement,
    };
    let res = await deliverStatusUpdate(payload);
    if (res?.data?.code === 200) {
      handleUpdateScheduleStatus("MAKER");
    } else {
      ErrorMessage(res?.data?.error);
    }
  };

  const handleInitPay = async () => {
    handleDeliveryStatus({
      id: initpay?.id,
      scheduleId: initpay?.scheduleId,
      payment: true,
      settlement: initpay?.isLast ? true : null,
    });
    setSettlement({
      status: false,
      id: null,
      data: false,
      initPayment: false,
      islast: false,
    });
  };

  let handleUpdateScheduleStatus = async (status) => {
    try {
      let res = await updateRepScheduleStatus({
        id: selectedScheduleId,
        status: status,
      });
      if (res?.data?.code === 200) {
        SuccessMessage(res?.data?.message);
        setSettlement({
          status: false,
          id: "",
          data: "",
          islast: false,
        });
        setConfirmModal({
          status: false,
          data: "",
          note: "",
        });
        dispatch(fetchSchedule(connectionId));
        dispatch(fetchSchedule(connectionId));
      }
    } catch (error) {
      console.error("Error updating schedule status:", error);
      ErrorMessage(error?.message || "Failed to update schedule status.");
    }
  };

  // Group schedules by scheduleId
  const groupedSchedules = useMemo(() => {
    const groups = {};
    data?.list?.forEach((schedule) => {
      const scheduleId = schedule.scheduleId;
      if (!groups[scheduleId]) {
        groups[scheduleId] = [];
      }
      groups[scheduleId].push(schedule);
    });
    return Object.values(groups);
  }, [data?.list]);

  return (
    <>
      {confirmModal?.status && (
        <ConfirmationModal
          open={confirmModal?.status}
          data={confirmModal?.data}
          note={confirmModal?.note}
          handleClose={() =>
            setConfirmModal({
              status: false,
              data: "",
              note: "",
            })
          }
          header={"Confirmation"}
          handleSubmit={handleInitPay}
        />
      )}
      {editSchedule && (
        <ScheduleUpdateModal
          type={typeCall}
          open={editSchedule}
          handleClose={() => setEditSchedule(false)}
          data={editData}
          blocks={blockLists}
          handleUpdateSubmit={
            typeCall === "edit" ? handleUpdateSubmit : handleDeliveredSubmit
          }
        />
      )}
      {settlement?.status && (
        <Settlement
          open={settlement?.status}
          handleClose={() =>
            setSettlement({
              status: false,
              id: "",
              data: "",
              initPayment: false,
              islast: false,
            })
          }
          id={connectionId}
          data={settlement?.data}
          handleDeliveryStatus={handleDeliveryStatus}
          isLast={settlement?.islast}
          selectedScheduleId={selectedScheduleId}
          connection={connection}
        />
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div className={`popupModal ${Styles.scheuleModalMainCont}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className="headingText">{"View Schedule"}</div>
            <Close
              style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>
          <DamStrip />
          <div className={`scroll_bar_y ${Styles.topScrollMainSection}`}>
            <div
              className={`display_flex_row tableHeading ${Styles.table_header_main}`}
            >
              <div style={{ width: "2%" }}></div>
              <div style={{ width: "15%" }}>Entity Name</div>
              <div style={{ width: "15%" }}>Delivery Date</div>
              <div style={{ width: "10%" }}>Time</div>
              <div style={{ width: "10%" }}>Schedule Vol. (MW)</div>
              <div style={{ width: "10%" }}>Schedule Vol. (MWh)</div>
              <div style={{ width: "10%" }}>Deliver Vol. (MWh)</div>
              <div style={{ width: "10%" }}>Rate(₹/kWh)</div>
              <div style={{ width: "10%" }}>
                <span>Activity</span>
                <Tooltip
                  title="Edit Button will be disabled 2 hours prior to schedule start time. Confirm Button will be enabled only after schedule end time."
                  style={{}}
                >
                  <IconButton>
                    <InfoIcon style={{ width: "14px", height: "16px" }} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            <div className={Styles.listing_acc_head}>
              {groupedSchedules
                // Filter out groups that only contain "ONE_TIME" schedules
                .map((scheduleGroup) =>
                  scheduleGroup.filter(
                    (schedule) => schedule?.type !== "ONE_TIME",
                  ),
                )
                .filter((filteredGroup) => filteredGroup.length > 0)
                .map((filteredGroup, groupIndex) => {
                  return (
                    <div
                      key={filteredGroup[0]?.scheduleId || groupIndex}
                      className={Styles.scheduleGroup}
                      style={{
                        border: "1px solid var(--primary-color-text)",
                        borderRadius: "10px",
                        marginBottom: "16px",
                        padding: "8px",
                      }}
                    >
                      {filteredGroup.map((schedule, index) => {
                        const absoluteIndex = data?.list?.findIndex(
                          (s) => s.id === schedule.id,
                        );
                        const { showEdit, showConfirm } = getButtonVisibility(
                          schedule?.fromDate,
                          schedule?.fromBlock?.timeBlock?.split("-")[0],
                          schedule?.toBlock?.timeBlock?.split("-")[1],
                        );
                        let islast = data?.list?.length === absoluteIndex + 1;

                        return (
                          <Accordion
                            key={schedule.id}
                            expanded={expanded === absoluteIndex}
                            onChange={() =>
                              handleAccordionChange(absoluteIndex, schedule?.id)
                            }
                          >
                            <AccordionSummary>
                              <div
                                className={`display_flex_row tableDetails ${Styles.accordionSummaryCustom}`}
                              >
                                <div style={{ width: "2%" }}>
                                  <ExpandMoreIcon
                                    className={`${Styles.expandIconLeft} ${
                                      expanded === absoluteIndex
                                        ? Styles.iconExpanded
                                        : ""
                                    }`}
                                  />
                                </div>
                                <div style={{ width: "15%" }}>
                                  {schedule?.counterPartyEntityName || "-"}
                                </div>
                                <div style={{ width: "15%" }}>
                                  {convertDate(schedule?.fromDate)}
                                </div>
                                <div style={{ width: "10%" }}>
                                  {
                                    schedule?.fromBlock?.timeBlock?.split(
                                      "-",
                                    )[0]
                                  }{" "}
                                  -{" "}
                                  {schedule?.toBlock?.endTime === "23:59:59"
                                    ? "24:00"
                                    : schedule?.toBlock?.timeBlock?.split(
                                        "-",
                                      )[1]}
                                </div>
                                <div style={{ width: "10%" }}>
                                  {(
                                    schedule?.scheduleVolume /
                                    (schedule?.totalBlock / 4)
                                  )?.toFixed(2) || "-"}
                                </div>
                                <div style={{ width: "10%" }}>
                                  {schedule?.scheduleVolume?.toFixed(2) || "-"}
                                </div>
                                <div style={{ width: "10%" }}>
                                  {schedule?.deliveredVolume?.toFixed(2)
                                    ? schedule?.deliveredVolume?.toFixed(2)
                                    : "-"}
                                </div>
                                <div style={{ width: "10%" }}>
                                  {schedule?.calculatedDamPrice ? (
                                    <div>
                                      <div>
                                        {schedule?.calculatedDamPrice?.toFixed(
                                          3,
                                        )}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "8px",
                                          color: "#f1935c",
                                        }}
                                      >
                                        {schedule?.energyRateType === "FIXED"
                                          ? `${schedule?.energyRate?.toFixed(3)}`
                                          : schedule?.energyRateType ===
                                              "DAM_PLUS"
                                            ? `DAM+${schedule?.energyRate}`
                                            : `DAM-${schedule?.energyRate}`}
                                        {`(DAM = ${
                                          schedule?.energyRateType ===
                                          "DAM_PLUS"
                                            ? `${
                                                (
                                                  schedule?.calculatedDamPrice -
                                                  schedule?.energyRate
                                                ).toFixed(3) || 0
                                              }`
                                            : `${
                                                (
                                                  schedule?.calculatedDamPrice +
                                                  schedule?.energyRate
                                                ).toFixed(3) || 0
                                              }`
                                        })`}
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      {schedule?.energyRateType === "FIXED"
                                        ? `${schedule?.energyRate?.toFixed(3)}`
                                        : schedule?.energyRateType ===
                                            "DAM_PLUS"
                                          ? `DAM+${schedule?.energyRate}`
                                          : `DAM-${schedule?.energyRate}`}
                                    </>
                                  )}
                                </div>
                                <div
                                  style={{ width: "10%" }}
                                  className={`display_flex_row_gap_8 ${Styles.action_button_cont}`}
                                >
                                  {user === "consumer" ? (
                                    <>
                                      {schedule?.paymentInitiated ? (
                                        <>
                                          {" "}
                                          <div
                                            className={`primary-button display_flex_row_gap_8 ${Styles.actionButtons}`}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setInitpay({
                                                id: schedule?.id,
                                                scheduleId:
                                                  schedule?.scheduleId,
                                                isLast: schedule?.isLast,
                                              });
                                              setSettlement({
                                                status: true,
                                                id: schedule?.id,
                                                data: schedule,
                                                islast: islast,
                                              });
                                              setSelectedScheduleId(
                                                schedule?.id,
                                              );
                                            }}
                                          >
                                            View Transaction
                                          </div>
                                          <div
                                            className={`primary-button display_flex_row_gap_8 ${Styles.actionButtons}`}
                                            onClick={(e) =>
                                              handleGenerateObligation({
                                                e: e,
                                                id: connectionId,
                                              })
                                            }
                                          >
                                            Obligation Report
                                          </div>
                                        </>
                                      ) : schedule?.settlement &&
                                        schedule?.isLast ? (
                                        <>
                                          <div
                                            className={`primary-button display_flex_row_gap_8 ${Styles.actionButtons}`}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setInitpay({
                                                id: schedule?.id,
                                                scheduleId:
                                                  schedule?.scheduleId,
                                                isLast: schedule?.isLast,
                                              });
                                              setSettlement({
                                                status: true,
                                                id: schedule?.id,
                                                data: schedule,
                                                islast: islast,
                                              });
                                            }}
                                          >
                                            Inititate Payment
                                          </div>
                                        </>
                                      ) : schedule?.deliveredVolume ||
                                        schedule?.deliveredVolume === 0 ? (
                                        <>
                                          {schedule?.isLast ? (
                                            <div
                                              className={`primary-button display_flex_row_gap_8 ${Styles.actionButtons}`}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setSettlement({
                                                  status: true,
                                                  id: schedule?.id,
                                                  data: schedule,
                                                  islast: islast,
                                                });
                                                setSelectedScheduleId(
                                                  schedule?.id,
                                                );
                                              }}
                                            >
                                              Settlement
                                            </div>
                                          ) : (
                                            <div
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setInitpay({
                                                  id: schedule?.id,
                                                  scheduleId:
                                                    schedule?.scheduleId,
                                                  isLast: schedule?.isLast,
                                                });
                                                setSettlement({
                                                  status: true,
                                                  id: schedule?.id,
                                                  data: schedule,
                                                  initPayment: true,
                                                  islast: islast,
                                                });
                                                setSelectedScheduleId(
                                                  schedule?.id,
                                                );
                                              }}
                                              className={`primary-button display_flex_row_gap_8 ${Styles.actionButtons}`}
                                            >
                                              Inititate Payment
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          {process.env.REACT_APP_ENV === "qa" ||
                                          process.env.REACT_APP_ENV ===
                                            "stage" ? (
                                            <>
                                              {" "}
                                              <div
                                                style={{
                                                  border: "1px solid #bdbdbd",
                                                  borderRadius: "6px",
                                                }}
                                                className={` display_flex_row_gap_8 ${Styles.buttonList}`}
                                                onClick={(e) =>
                                                  handleEditSchedule(
                                                    e,
                                                    schedule,
                                                    "edit",
                                                  )
                                                }
                                              >
                                                <span>Edit</span>
                                              </div>
                                              {schedule?.isAmountBlocked && (
                                                <div
                                                  className={`primary-button ${Styles.buttonList}`}
                                                  onClick={(e) =>
                                                    handleEditSchedule(
                                                      e,
                                                      schedule,
                                                      "deliver",
                                                    )
                                                  }
                                                >
                                                  Confirm
                                                </div>
                                              )}
                                            </>
                                          ) : showEdit ? (
                                            <div
                                              className={`primary-button display_flex_row_gap_8 ${Styles.buttonList}`}
                                              onClick={(e) =>
                                                handleEditSchedule(
                                                  e,
                                                  schedule,
                                                  "edit",
                                                )
                                              }
                                            >
                                              <span>Edit</span>
                                            </div>
                                          ) : showConfirm &&
                                            schedule?.isAmountBlocked ? (
                                            <div
                                              className={`primary-button ${Styles.buttonList}`}
                                              onClick={(e) =>
                                                handleEditSchedule(
                                                  e,
                                                  schedule,
                                                  "deliver",
                                                )
                                              }
                                            >
                                              Confirm
                                            </div>
                                          ) : (
                                            "-"
                                          )}
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {schedule?.paymentInitiated ? (
                                        <>
                                          {" "}
                                          <div
                                            className={`primary-button display_flex_row_gap_8 ${Styles.actionButtons}`}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setInitpay({
                                                id: schedule?.id,
                                                scheduleId:
                                                  schedule?.scheduleId,
                                                isLast: schedule?.isLast,
                                              });
                                              setSettlement({
                                                status: true,
                                                id: schedule?.id,
                                                data: schedule,
                                              });
                                              setSelectedScheduleId(
                                                schedule?.id,
                                              );
                                            }}
                                          >
                                            View Transaction
                                          </div>
                                          <div
                                            className={`primary-button display_flex_row_gap_8 ${Styles.actionButtons}`}
                                            onClick={(e) =>
                                              handleGenerateObligation({
                                                e: e,
                                                id: connectionId,
                                              })
                                            }
                                          >
                                            Obligation Report
                                          </div>
                                        </>
                                      ) : (
                                        <div className="pending">Pending</div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </AccordionSummary>
                            <AccordionDetails>
                              <div className={Styles.summaryMainCont}>
                                <div
                                  className={`display_flex_row tableHeading_type2`}
                                >
                                  <div style={{ width: "15%" }}>Start Time</div>
                                  <div style={{ width: "15%" }}>End Time</div>
                                  <div style={{ width: "20%" }}>
                                    Schedule Volume (MWh)
                                  </div>
                                  <div
                                    style={{
                                      width: "20%",
                                      textWrap: "no-wrap",
                                    }}
                                  >
                                    Deliver Volume (MWh)
                                  </div>
                                  <div style={{ width: "15%" }}>Status</div>
                                </div>
                                <div
                                  className={`scroll_bar_y ${Styles.bottomScrollMain}`}
                                >
                                  {blockLists?.length > 0 &&
                                    blockLists?.map((list) => (
                                      <div
                                        className={`display_flex_row tableDetails`}
                                      >
                                        <div style={{ width: "15%" }}>
                                          {list?.timeBlock?.split("-")?.[0]}
                                        </div>
                                        <div style={{ width: "15%" }}>
                                          {list?.timeBlock
                                            ?.split("-")?.[1]
                                            ?.trim() === "23:59:59"
                                            ? "24:00"
                                            : list?.timeBlock?.split("-")?.[1]}
                                        </div>
                                        <div style={{ width: "20%" }}>
                                          {list?.scheduleVolume !== undefined &&
                                          list?.scheduleVolume !== null
                                            ? `${formatToDecimals(
                                                list?.scheduleVolume,
                                              )} (MWh)`
                                            : "-"}
                                        </div>
                                        <div style={{ width: "20%" }}>
                                          {list?.deliveredVolume !==
                                            undefined &&
                                          list?.deliveredVolume !== null
                                            ? `${formatToDecimals(
                                                list?.deliveredVolume,
                                              )} (MWh)`
                                            : "-"}
                                        </div>
                                        <div style={{ width: "15%" }}>
                                          {list?.status === "COMPLETED" ? (
                                            <div
                                              className={`display_flex_row_center accepted`}
                                            >
                                              Completed
                                            </div>
                                          ) : (
                                            <div
                                              className={`display_flex_row_center pending`}
                                            >
                                              Pending
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ScheduleModal;
